const removeNonInteractionKeyValuePair = (object: any) => {
  if (!object) return;
  return Object.keys(object).reduce((acc: any, key) => {
    return {
      ...acc,
      ...(key !== 'nonInteraction' && { [`${key}`]: object[key] }),
    };
  }, {});
};

/**
 * Checks if an object is empty.
 * @param object
 * @returns {boolean}
 */

export const objectEmpty = (object: Object) => {
  return (
    object &&
    Object.keys(object).length === 0 &&
    Object.getPrototypeOf(object) === Object.prototype
  );
};

/**
 * Removes all undefined key value pairs from an object.
 * @param object
 * @returns {object}
 */

export const removeUndefinedKeyValuePairs = (object: any): any => {
  if (!object) return;
  const objectWithoutUndefinedValues: any = {};
  Object.keys(object).forEach((key: string) => {
    const value: any = object[key];
    if (value !== undefined && value !== null && !objectEmpty(value))
      objectWithoutUndefinedValues[key] = value;
  });
  return objectWithoutUndefinedValues;
};

/**
 * Finds a value by key in an object.
 * @param object
 * @param keyToFind
 * @returns
 */
export const findValueByKeyInObject = (object: any, keyToFind: string): any => {
  if (!object) return;
  const objectWhereKeyResides = findObjectWhereKeyResides(object, keyToFind);
  if (!objectWhereKeyResides) return;
  return objectWhereKeyResides[keyToFind];
};

/**
 * Recursively searches an object hierarchy until it finds the object that contains the specific primitive value.
 * @param object
 * @param keyToFind
 * @returns
 */
export const findObjectWhereKeyResides = (
  object: any,
  keyToFind: string
): any => {
  if (isObject(object)) {
    const keys = Object.keys(object);
    if (keys.includes(keyToFind)) {
      return object;
    }
    for (const key of keys) {
      const result = findObjectWhereKeyResides(object[key], keyToFind);
      if (result) return result;
    }
    return;
  } else if (Array.isArray(object)) {
    for (const entry of object) {
      findObjectWhereKeyResides(entry, keyToFind);
    }
  }
  return;
};

/**
 * Flattens an array with objects or an object with objects to a single-depth object hierarchy.
 * Used to flatten for example a payload that contains an array of item objects.
 * This is used because the insightsApi only accepts a single-depth object hierarchy with primitives.
 * @example [{itemId: '8080830'}, itemId: {itemId: '9093020'}] becomes {itemId_1: '8080830', itemId_2: '9093020'}
 * @param object
 * @param parent
 * @param result
 * @returns A single depth object hierarchy with primitives.
 */

export const flattenObject = (
  object: any,
  parent: string = '',
  result: any = {}
) => {
  if (!object) return result;

  const isArray = (value: unknown) => {
    return Array.isArray(value);
  };

  const getReturn = (key: string): any => {
    const propName = parent ? `${parent}_${key}` : key;
    const value: any = object[key];

    if (isObject(value)) return flattenObject(value, propName, result);

    return {
      [propName]: isArray(value)
        ? value.map((item: any) => flattenObject(item, propName, result))
        : value,
    };
  };

  return Object.keys(object).reduce(
    (acc: any, curr: string) => ({ ...acc, ...getReturn(curr) }),
    result
  );
};
/**
 * Removes all undefined key value pairs.
 * @param object
 * @returns
 */
export const sanitizeObject = (object: any) => {
  if (!object) return;
  return removeNonInteractionKeyValuePair(removeUndefinedKeyValuePairs(object));
};

/**
 * Checks to see if the meta object contains a property nonInteraction which is true.
 * @param meta
 * @returns {boolean}
 */
export const isNonInteractionEvent = (object: any) => {
  return object?.nonInteraction;
};

const isObject = (value: unknown) => {
  return typeof value === 'object' && !Array.isArray(value) && value !== null;
};
